/**
 * StripeConnectInfoTip — BYOK-style instructional eye-tooltip for Stripe Connect.
 *
 * Mirrors the pattern of <KeyInstructionsPopover> but for Stripe Express:
 *   1. Live status pill (Stripe ready ✓ / Onboarding pending / Not connected).
 *   2. Step-by-step explanation of "what happens when a fan pays $X".
 *   3. Inline "Connect Stripe →" button when not yet onboarded — kicks off
 *      the same /api/stripe/connect/start flow as Account & Billing.
 *
 * Used in two places per user request:
 *   - SiloSettings → Monetization card (next to the price input)
 *   - Operations / Account & Billing → next to "Platform key — What is this?"
 *
 * Stripe Connect Express does NOT take a pasted API key the way BYOK does
 * (it's a hosted onboarding flow), so this component intentionally has no
 * paste-and-save input — it has a "Go to Stripe" button instead.
 */
import { useEffect, useState } from "react";
import { CreditCard, ArrowSquareOut, CheckCircle, Clock, Warning, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import { supporterFeeCents, SUPPORTER_FEE_DEFAULTS } from "@/lib/supporterFee";

export default function StripeConnectInfoTip({
  // What price/amount this tooltip is contextualizing (renders the example math).
  // Pass cents — e.g. 300 for "$3/mo".
  exampleCents = null,
  // Tooltip placement (defaults to bottom-right since most callers attach it
  // to a label at the top of a card).
  placement = "bottom-right",
  width = 440,
  testid = "stripe-connect-info",
  // Optional: parent can pass a label override (default: "Stripe & payouts help").
  label = "How does payment work?",
  // Variant — "monetization" tweaks the body to talk about silo pricing,
  // "platform" talks about platform-side payouts.
  variant = "monetization",
}) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/stripe/config");
        if (!cancel) setCfg(r.data);
      } catch { /* silent — tooltip still renders without status */ }
    })();
    return () => { cancel = true; };
  }, []);

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/start", { origin_url: window.location.origin });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No onboarding URL returned");
      }
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Couldn't open Stripe onboarding.");
    }
  };

  const status = cfg?.charges_enabled
    ? "ready"
    : cfg?.has_account
      ? "pending"
      : "none";

  const StatusPill = () => {
    if (!cfg) return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] bg-[#F0F1EE] px-1.5 py-0.5 rounded">
        <Spinner size={10} className="animate-spin" /> Loading…
      </span>
    );
    if (status === "ready") return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#2F6B3A] bg-[#E8F0E9] px-1.5 py-0.5 rounded">
        <CheckCircle size={10} weight="fill" /> Stripe ready
      </span>
    );
    if (status === "pending") return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#8A5A00] bg-[#F8F1E9] px-1.5 py-0.5 rounded">
        <Clock size={10} weight="fill" /> Onboarding pending
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#A85E4A] bg-[#FCEDE6] px-1.5 py-0.5 rounded">
        <Warning size={10} weight="fill" /> Not connected
      </span>
    );
  };

  // Example-math line — Sep 25, 2026 (F6). This used to run the application
  // fee against the creator's price and tell them "of $3 you keep $2.85",
  // which stopped being true when the Supporter Service Fee shipped: the
  // supporter now pays that fee ON TOP, so the creator keeps their full price.
  // Reads the one shared calculator (`lib/supporterFee.js`), same numbers the
  // subscribe modal and the public pill quote.
  const exampleMath = (() => {
    if (!exampleCents || !cfg) return null;
    const feeCfg = {
      revenue_bps: cfg.your_fan_cut_bps ?? SUPPORTER_FEE_DEFAULTS.revenue_bps,
      processing_bps: cfg.estimated_processing_bps ?? SUPPORTER_FEE_DEFAULTS.processing_bps,
      fixed_cents: cfg.estimated_processing_fixed_cents ?? SUPPORTER_FEE_DEFAULTS.fixed_cents,
    };
    const feeCents = supporterFeeCents(exampleCents, feeCfg);
    const fmt$ = (c) => `$${(c / 100).toFixed(2)}`;
    return (
      <div className="bg-[#F4F2EC] border border-[#1C1B1A]/10 rounded-md p-2.5 mt-2.5 text-[#1C1B1A]">
        <div className="text-[9px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1">Example for {fmt$(exampleCents)}/mo</div>
        <div className="grid grid-cols-3 gap-1.5 text-[11px] tabular-nums">
          <div><div className="text-[#6B6A68] text-[10px]">Fan pays</div><div className="font-medium" data-testid="connect-example-total">{fmt$(exampleCents + feeCents)}</div></div>
          <div><div className="text-[#6B6A68] text-[10px]">Service fee (theirs)</div><div className="font-medium text-[#A85E4A]" data-testid="connect-example-fee">{fmt$(feeCents)}</div></div>
          <div><div className="text-[#6B6A68] text-[10px]">You keep</div><div className="font-medium text-[#2F6B3A]" data-testid="connect-example-keep">{fmt$(exampleCents)}</div></div>
        </div>
        <div className="text-[9px] text-[#6B6A68] mt-1.5">
          The supporter pays My Silo&apos;s fee on top, so 100% of your price reaches you.
        </div>
      </div>
    );
  })();

  return (
    <InfoTip
      icon="eye" iconSize={14}
      label={label}
      placement={placement}
      width={width}
      testid={testid}
    >
      <div className="text-[#FDFCF8]">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard size={14} weight="duotone" />
          <strong className="text-[12px]">
            {variant === "monetization" ? "Stripe & paying creators" : "Platform-key vs Stripe payouts"}
          </strong>
          <span className="ml-auto"><StatusPill /></span>
        </div>

        {variant === "monetization" ? (
          <>
            <div className="text-[#FDFCF8]/85 mb-2 text-[11px]">
              Fans pay directly through Stripe. We never touch their card —
              Stripe charges them your price plus the My Silo supporter service
              fee, keeps that fee as the platform&apos;s application fee, and wires
              your full price to your own Stripe account on a regular payout
              schedule. You set the price here; Stripe does the math at fan
              checkout time.
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#FDFCF8]/50 mb-1">How to set it up</div>
            <ol className="text-[11px] text-[#FDFCF8]/95 leading-snug list-decimal list-inside space-y-0.5 mb-2">
              <li>Set your subscriber price below. $0 = free public access.</li>
              <li>Click <strong>Connect Stripe</strong> (or finish onboarding if pending).</li>
              <li>Stripe Express opens — fill out the form on Stripe's site.</li>
              <li>You'll be redirected back here. Status flips to <em>Stripe ready</em>.</li>
              <li>Fans now check out via Stripe; payouts land in your bank.</li>
            </ol>
            {exampleMath}
          </>
        ) : (
          <>
            <div className="text-[#FDFCF8]/85 mb-2 text-[11px]">
              Two distinct payment surfaces:
              <br />• <strong>Platform key / BYOK</strong> — controls how LLM API calls get billed (your own Google key vs. the platform's direct provider keys).
              <br />• <strong>Stripe Connect</strong> — controls how fans pay you and how YOU receive money.
              <br />They're independent: a creator can have BYOK without Stripe, or Stripe without BYOK, or both, or neither.
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#FDFCF8]/50 mb-1">When to set up Stripe</div>
            <ul className="text-[11px] text-[#FDFCF8]/95 leading-snug list-disc list-inside space-y-0.5 mb-2">
              <li>You want to charge fans for any silo subscription.</li>
              <li>You want to receive tips from fans.</li>
              <li>You're enrolling in Creator Pro and want cash payouts.</li>
            </ul>
            <div className="text-[10px] text-[#FDFCF8]/60 italic">If everything you run is free, you can skip Stripe entirely.</div>
          </>
        )}

        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
          {status === "ready" ? (
            <a
              href="/dashboard/account-settings"
              className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#2F6B3A] text-[#FDFCF8] hover:bg-[#235029] inline-flex items-center gap-1"
              data-testid={`${testid}-manage-link`}
            >
              <ArrowSquareOut size={11} /> Manage in Account & Billing
            </a>
          ) : (
            <button
              type="button"
              onClick={startConnect}
              disabled={busy}
              data-testid={`${testid}-connect-btn`}
              className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#C16E5A] text-[#FDFCF8] hover:bg-[#A85E4A] inline-flex items-center gap-1 disabled:opacity-60"
            >
              {busy ? "Opening Stripe…" : <>{status === "pending" ? "Continue Stripe onboarding" : "Connect Stripe"} <ArrowSquareOut size={11} /></>}
            </button>
          )}
        </div>
      </div>
    </InfoTip>
  );
}
